import { useState } from "react"
import Button from "@mui/material/Button"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import Box from "@mui/system/Box"
import { Add } from "@mui/icons-material"
import { PaymentConcept, PaymentItem } from '@/types/payment.types';
import { Student } from '@/types/user.types';

interface ItemsSelectorProps {
  handleAddItem: ({ item }: { item: PaymentConcept }) => void
  paymentConcepts: PaymentConcept[]
  students: Student[]
  itemsToPay: PaymentConcept[]
}

const ItemsSelector: React.FC<ItemsSelectorProps> = ({ handleAddItem, paymentConcepts, students, itemsToPay }) => {
  const [selectedItem, setSelectedItem] = useState<PaymentConcept>(paymentConcepts[0]);
  const [itemValue, setItemValue] = useState<number>(10000);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);

  // Check if the current combination of item + student already exists
  const itemAlreadyAdded = selectedItem && selectedStudent
    ? itemsToPay.some(item => item.id === selectedItem.id && item.studentId === selectedStudent.id)
    : false;

  return <Box sx={{
    display: "flex",
    width: { xs: "100%", md: "40%" },
    flexDirection: "column",
    borderRight: { xs: "none", md: "2px solid #E0E0E0" },
    borderBottom: { xs: "2px solid #E0E0E0", md: "none" },
    pb: { xs: 3, md: 0 }
  }}>
    <Box sx={{ py: 0.5, mx: { xs: 0, md: 4, lg: 6 } }}>
      <Typography
        variant="subtitle2"
        gutterBottom
        fontWeight={600}
        sx={{ fontSize: { xs: "0.875rem", md: "0.95rem" } }}
      >
        Seleccione un ítem de pago
      </Typography>
      <Select
        fullWidth
        value={selectedItem?.name || ""}
        onChange={(e) => {
          const item = paymentConcepts.find(item => item.name === e.target.value);
          if (item) {
            setSelectedItem(item || {} as PaymentItem);
            setItemValue(item?.amount || 0);
          }

        }}
        sx={{
          mb: 3,
          fontSize: { xs: "0.875rem", md: "1rem" }
        }}
      >
        {paymentConcepts.map((item) => (<MenuItem key={item.id} value={item.name}>{item.name}</MenuItem>))}
      </Select>
    </Box>
    <Box sx={{ py: 0.5, mx: { xs: 0, md: 4, lg: 6 } }}>
      <Typography
        variant="subtitle2"
        gutterBottom
        fontWeight={600}
        sx={{ fontSize: { xs: "0.875rem", md: "0.95rem" } }}
      >
        Valor
      </Typography>
      <TextField
        onChange={(e) => setItemValue(parseInt(e.target.value))}
        fullWidth
        value={itemValue}
        disabled={selectedItem?.name !== "Otros"}
        type="number"
        InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
        sx={{
          mb: 3,
          '& input': { fontSize: { xs: "0.875rem", md: "1rem" } }
        }}
      />
    </Box>
    <Box sx={{ py: 0.5, mx: { xs: 0, md: 4, lg: 6 } }}>
      <Typography
        variant="subtitle2"
        gutterBottom
        fontWeight={600}
        sx={{ fontSize: { xs: "0.875rem", md: "0.95rem" } }}
      >
        Seleccione estudiante
      </Typography>
      <Select
        fullWidth
        value={selectedStudent?.id || ""}
        onChange={(e) => {
          const student = students.find(s => s.id === e.target.value);
          setSelectedStudent(student || null);
          if (selectedItem && student) {
            setSelectedItem({
              ...selectedItem,
              studentId: student.id,
              studentName: student.name
            })
          }
        }}
        sx={{
          mb: 3,
          fontSize: { xs: "0.875rem", md: "1rem" }
        }}
      >
        {students.map((student) => (
          <MenuItem key={student.id} value={student.id}>
            {student.name}
          </MenuItem>
        ))}
      </Select>
    </Box>
    <Box sx={{
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-end",
      mx: { xs: 0, md: 4, lg: 6 },
    }}>
      <Button
        variant="contained"
        disabled={!selectedStudent || itemAlreadyAdded}
        onClick={() => {
          setSelectedStudent(null);
          handleAddItem({
            item: {
              ...selectedItem,
              amount: itemValue
            } as PaymentItem
          });
        }}
        startIcon={<Add />}
        sx={{
          color: "white",
          height: { xs: "25px", md: "30px", lg: "40px" },
          width: { xs: "100%", md: "50%", lg: "45%" },
          backgroundColor: '#2C3E50',
          '&:hover': { backgroundColor: '#1a252f' },
          textTransform: 'none',
          py: { xs: 1.2, md: 1.5 },
          fontSize: { xs: "0.75rem", md: "0.85rem", lg: "0.95rem" },
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {itemAlreadyAdded ? 'Agregado' : 'Agregar'}
      </Button>
    </Box>
  </Box>
}

export default ItemsSelector